/**
 * Editing a route-sheet draft — #90, the passenger half.
 *
 * A sibling of `DecaEditScreen`, not a parameterised version of it. The two screens read
 * different endpoints, hand their draft to different forms and title themselves from different
 * catalogue keys, and the URL is what settles which document family is being edited — so nothing
 * here has to ask an id what it is. Making the two arms symmetric is this module's standing
 * failure mode; what they share is the *rule*, not the code.
 *
 * Mounted on the **write** surface although the sheet it edits is read behind the other guard:
 * editing is a write, and the router draws that line structurally instead of leaving it to a
 * conditional inside a read screen.
 *
 * The screen itself is only a loader: it reads the draft with its `ETag` and hands both to
 * `PassengerCreateForm`, which is the same form that created it. The `ETag` matters because
 * every write against an existing sheet declares `If-Match` **required**, and
 * `GET /passenger-decas/{sheetId}` is the only place one is handed out.
 */

import { Navigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../app/auth.tsx'
import { usePassengerDeca } from '../app/queries.ts'
import { isGenerated } from '../deca/estado.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Card, ErrorState, LoadingRows, NoAccessState } from '../ui/primitives.tsx'
import { PassengerCreateForm } from './PassengerCreateForm.tsx'

export function PassengerEditScreen() {
  const { t } = useTranslation()
  const { sheetId } = useParams({ strict: false }) as { sheetId: string }
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)

  const query = usePassengerDeca(sheetId)
  const sheet = query.data?.data
  const etag = query.data?.etag ?? undefined

  if (!abilities.writeDecas) {
    return (
      <AppShell title={t('passengerDetail.draftTitle')}>
        <Card>
          <NoAccessState />
        </Card>
      </AppShell>
    )
  }

  if (query.isLoading) {
    return (
      <AppShell title={t('passengerDetail.draftTitle')}>
        <Card>
          <LoadingRows rows={6} columns={3} />
        </Card>
      </AppShell>
    )
  }

  if (query.error || !sheet) {
    return (
      <AppShell title={t('passengerDetail.draftTitle')}>
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      </AppShell>
    )
  }

  // "Only while `BORRADOR`". An issued route sheet is never edited in place: it changes through
  // *Modificar* (BOE Fifth.1) or *Sustituir* (Fifth.2), and Seventh voids any other alteration.
  // `PATCH /passenger-decas/{sheetId}` answers 409 for exactly this, but nobody should have to
  // submit a form to be told, and a URL typed, bookmarked or followed after the sheet was issued
  // must not open an editor over it. Sent to the sheet rather than shown a refusal: there is
  // nothing to edit here, and everything it is legitimate to do to an issued sheet is on that
  // screen. `replace`, so Back does not bounce into the editor.
  if (isGenerated(sheet)) {
    return <Navigate to="/hojas-de-ruta/$sheetId" params={{ sheetId }} replace />
  }

  return (
    <AppShell title={t('passengerDetail.draftTitle')}>
      <PassengerCreateForm draft={sheet} etag={etag} />
    </AppShell>
  )
}
